import {
  normalizeTranslationLocale,
  normalizeTranslationSourceLocale,
  shouldSkipTranslation,
  type TranslationLocale,
  type TranslationSourceLocale,
} from './language';

export interface TranslationCacheRecord {
  translatedText: string;
  sourceLang: string;
  targetLang: string;
  cachedAt: number;
}

const CACHE_STORAGE_KEY = 'jema-translation-cache-v1';
const CACHE_MAX_ENTRIES = 2000;
const GTX_ENDPOINT = 'https://translate.googleapis.com/translate_a/single';

const memoryCache = new Map<string, TranslationCacheRecord>();
let storageLoaded = false;

const hashKey = (input: string): string => {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16);
};

const buildCacheKey = (
  text: string,
  sourceLang: TranslationSourceLocale,
  targetLang: TranslationLocale
): string => hashKey(`${sourceLang}|${targetLang}|${text}`);

const ensureStorageLoaded = () => {
  if (storageLoaded || typeof window === 'undefined') return;
  storageLoaded = true;
  try {
    const raw = window.localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, TranslationCacheRecord>;
    Object.entries(parsed).forEach(([key, value]) => {
      if (value && typeof value.translatedText === 'string') memoryCache.set(key, value);
    });
  } catch {
    // ignore corrupt cache
  }
};

const persistCache = () => {
  if (typeof window === 'undefined') return;
  try {
    const entries = Array.from(memoryCache.entries());
    const trimmed = entries.slice(Math.max(0, entries.length - CACHE_MAX_ENTRIES));
    window.localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(Object.fromEntries(trimmed)));
  } catch {
    // ignore quota errors
  }
};

const setCacheValue = (key: string, record: TranslationCacheRecord) => {
  memoryCache.set(key, record);
  if (memoryCache.size > CACHE_MAX_ENTRIES) {
    const overflow = memoryCache.size - CACHE_MAX_ENTRIES;
    Array.from(memoryCache.keys())
      .slice(0, overflow)
      .forEach(k => memoryCache.delete(k));
  }
  persistCache();
};

// Google response shape: [[[translatedChunk, sourceChunk, ...], ...], ...]
const parseGtxPayload = (payload: unknown): string | null => {
  if (!Array.isArray(payload)) return null;
  const chunks = payload[0];
  if (!Array.isArray(chunks)) return null;
  const translated = chunks
    .map(chunk => (Array.isArray(chunk) && typeof chunk[0] === 'string' ? chunk[0] : ''))
    .join('');
  return translated.length ? translated : null;
};

const fetchTranslatedJoined = async (
  joined: string,
  source: TranslationSourceLocale,
  target: TranslationLocale
): Promise<string> => {
  const params = new URLSearchParams({
    client: 'gtx',
    sl: source,
    tl: target,
    dt: 't',
    q: joined,
  });
  const response = await fetch(`${GTX_ENDPOINT}?${params.toString()}`, { method: 'GET' });
  if (!response.ok) throw new Error(`translate request failed: ${response.status}`);
  const parsed = parseGtxPayload(await response.json());
  return parsed ?? joined;
};

export const translateBatch = async (
  texts: string[],
  targetLang?: string,
  sourceLang?: string
): Promise<string[]> => {
  if (!texts.length) return [];
  if (!targetLang) return [...texts];

  const target = normalizeTranslationLocale(targetLang);
  const source = normalizeTranslationSourceLocale(sourceLang);
  if (shouldSkipTranslation(source, target)) return [...texts];

  ensureStorageLoaded();

  const result = [...texts];
  const missingIndexes: number[] = [];
  texts.forEach((text, index) => {
    const cached = memoryCache.get(buildCacheKey(text, source, target));
    if (cached) result[index] = cached.translatedText;
    else if (text.trim().length) missingIndexes.push(index);
  });

  if (!missingIndexes.length) return result;

  try {
    const joined = missingIndexes.map(i => texts[i]).join('\n');
    const translatedJoined = await fetchTranslatedJoined(joined, source, target);
    const pieces = translatedJoined.split('\n');
    missingIndexes.forEach((originalIndex, pos) => {
      const value = pieces[pos] ?? texts[originalIndex];
      result[originalIndex] = value;
      setCacheValue(buildCacheKey(texts[originalIndex], source, target), {
        translatedText: value,
        sourceLang: source,
        targetLang: target,
        cachedAt: Date.now(),
      });
    });
    return result;
  } catch {
    return [...texts];
  }
};

export const translate = async (
  text: string,
  targetLang?: string,
  sourceLang?: string
): Promise<string> => {
  const [translated] = await translateBatch([text], targetLang, sourceLang);
  return translated ?? text ?? '';
};

export const __clearTranslationCacheForTests = () => {
  memoryCache.clear();
  storageLoaded = false;
};
